import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  ModalBody,
} from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import { CollapsibleCore } from '../components/Misc/Collapsible';
import EditableLabel from '../components/Misc/EditableLabel';
import Modal from '../components/Modals/Modal.js';
import AddNoteTypeModal from '../components/Modals/BuilderSectionNoteModal.js';
import PdfUploadModal from '../components/Modals/pdfUploadModal';
import PdfSectionModal from '../components/Modals/PdfSectionModal.js';
import PdfSectionsTextModal from '../components/Modals/PdfSectionsTextModal.js';
import PdfInstructionsModal from 'components/Modals/PdfInstructionsModal';
import LinearProgressBar from '../components/Misc/LinearProgressBar';
import { useMediaQuery } from 'react-responsive';
import {
  getPageInfo,
  getHaveInjector,
} from '../EMR_Integration/react_webInjector.ts';

// core components
import Header from 'components/Headers/Header';
import { isCX } from 'api/DeviceInfo';

const FAILED_GENERATION_MESSAGE =
  'Looks like the generation failed. Please try again or contact support (support@comprehendpt.ai).';
const TIME_OUT_MESSAGE =
  'Generation is taking longer than expected. Please wait a few minutes!';
const N_EXAMPLES = 7;
const MIN_ONE_NOTE_TYPE_EXAMPLES = 3;
const INTRO = (
  <>
    <p>
      <strong>
        Upload at least 3 completed notes to train Comprehend to match your
        style!
      </strong>
    </p>

    <p>
      <strong>Step 1:</strong> Have at least 3 PDFs ready to upload
    </p>
    <p style={{ marginLeft: '20px' }}>
      - Make sure all PDFs are the same type (Initial Eval or Daily Note).
    </p>

    <p>
      <strong>Step 2:</strong> Review the extracted sections to make sure
      everything looks right
    </p>
  </>
);
const OVERWRITE_INSTRUCTION_STATUS =
  'Looks like you already used the template builder previously and generated a template with instructions! Once you click "Overwrite Instructions" you can no longer retrieve older instructions.';
const EXISTING_TEMPLATE_OVERWRITE_WARNING_STATUS =
  'You already have a template for this note type. If you click "Overwrite Instructions," you will no longer be able to retrieve the older instructions. Alternatively, you can click "View Template" to review the existing template and make a copy before overwriting.';
const OVERWRITE_SECTIONS_STATUS = `'Start Over' to re-analyse your PDFs or 'Resume' from where you left off.`;
const SECTION_EXTRACTION_STATUS = [
  'Hang tight! Giving your PDFs a thorough look...',
  'On the case, gathering all the key details...',
  'Zooming in on each section — almost there!',
  'Putting on the final touches for you...',
  'Done with the groundwork! Getting Ready for the next step!',
];
const SECTION_TEXT_EXTRACTION_STATUS = [
  'Scanning your PDFs — this might take a moment...',
  'Extracting the text from each section...',
  'Matching content to fit your sections just right...',
  'Putting on the final polish, almost done...',
  'Done with the groundwork! Getting Ready for the next step!',
];
const SECTION_INSTRUCTION_STATUS = [
  'Gathering just the right sections for your template...',
  'Analyzing content for that perfect fit...',
  'Adding a personalized touch with AI magic...',
  'Polishing up to match your unique style...',
  'Done with the groundwork! Getting Ready for the next step!',
];
const TEMPLATE_GENERATION_STATUS = [
  'Updating instructions...',
  'Preparing your custom template...',
  'Finalizing setup...',
  'Your template will be ready soon!',
  'Groundwork complete, launching your custom template soon!',
];

const PdfTemplateBuilder = () => {
  const navigate = useNavigate();

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);
  const isSmallScreen = useMediaQuery({ query: '(max-width: 600px)' });

  // State Variables
  const [noteTypeData, setNoteTypeData] = useState({});
  const [status, setStatus] = useState('Welcome to the PDF Template Builder.');

  const [modalOpen, setModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalHeader, setModalHeader] = useState('PDF Template Builder');
  const [modalButtons, setModalButtons] = useState('');
  const [canGenerateTemplate, setCanGenerateTemplate] = useState(false);
  const [sectionNames, setSectionNames] = useState([]);
  const [isPdfSectionModalOpen, setIsPdfSectionModalOpen] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    key: null,
    exampleIndex: null,
  });
  const [configId, setConfigId] = useState('');
  const [isAddNoteTypeModalOpen, setIsAddNoteTypeModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [sectionsText, setSectionsText] = useState([]);
  const [isPdfSectionsTextModalOpen, setIsPdfSectionsTextModalOpen] =
    useState(false);
  const [isPdfInstructionsModalOpen, setIsPdfInstructionsModalOpen] =
    useState(false);
  const [sectionsInstructions, setSectionsInstructions] = useState([]);

  // Check if every note type has N_EXAMPLES or more examples we can generate
  useEffect(() => {
    const allNoteTypeMeetMinimumRequirements = Object.values(
      noteTypeData,
    ).every((examples) => examples.length >= N_EXAMPLES);

    const atLeastOneNoteTypenMeetsThreshold = Object.values(noteTypeData).some(
      (examples) => examples.length >= MIN_ONE_NOTE_TYPE_EXAMPLES,
    );

    setCanGenerateTemplate(
      allNoteTypeMeetMinimumRequirements && atLeastOneNoteTypenMeetsThreshold,
    );
  }, [noteTypeData]);

  // Fetch PDF Builder Docs Function
  const fetchPDFBuilderDocs = async () => {
    try {
      let pdfBuilderDocs = await apiClient.getPDFBuilderDocs();
      if (pdfBuilderDocs && pdfBuilderDocs.length === 0) {
        setModalOpen(true);
        setModalHeader('');
        setModalText(INTRO);
        setModalButtons([
          {
            label: "Let's Get Started",
            color: 'primary',
            onClick: () => {},
          },
        ]);
      }
      // Sort data based on the length of configDocs array
      const sortedData = pdfBuilderDocs.sort((a, b) => {
        return a.configDocs.length - b.configDocs.length;
      });

      // Set the sorted data to noteTypeData
      setNoteTypeData(sortedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setModalOpen(true);
      setModalText('An error occurred while fetching data. Please try again.');
    }
  };

  // Trigger Fetch when component mounts
  useEffect(() => {
    fetchPDFBuilderDocs();
    setStatus(
      `Add Note Type to get started! You need at least ${MIN_ONE_NOTE_TYPE_EXAMPLES} pdfs per note type to generate a template.`,
    );
  }, []);

  // Help Button
  const openHelpModal = () => {
    setModalOpen(true);
    setModalText(
      'After working with hundreds of clinicians we found that every one of them had their own way of documenting so we built templates to allow you to customize your notes. What we found was instructions and examples were cumbersome and confusing, making onboarding difficult and time consuming for new users. We built the pdf template builder to allow you to create your own templates just by loading in your existing pdf notes. We use AI to analyze your existing notes and write your instructions for you.',
    );
  };

  // Add a new note type to the template
  const addNoteType = async (noteType) => {
    if (!noteType) {
      return;
    }

    try {
      // Make the API call to add the note type
      let emrUrl = '';
      const haveInjector = await getHaveInjector();
      /* getHaveInjector returns if we have integration with the current EMR or not.
      If HaveInjector is true then we pass emrURL otherwise it is assumed to be a MicDrop and we skip emrUrl value */
      if (isCX && haveInjector) {
        const pageInfo = await getPageInfo();
        if (pageInfo['error'] && pageInfo['error'] === 'No Injector Found') {
          alert('Please load up your EMR. Try Refreshing');
          return;
        }
        emrUrl = pageInfo['URL'] || '';
      }
      const response = await apiClient.addNoteType(noteType, emrUrl);

      if (response && response.error) {
        alert(
          `Config with the name  '${noteType}' already exists. Please try a different name.`,
        );
        return;
      }
      let configID = response[response.length - 1].configId;
      setConfigId(configID);
      setIsPdfModalOpen(true);
      fetchPDFBuilderDocs();
    } catch (error) {
      console.error('Error adding note type:', error);
    }
  };

  const handleTemplateGeneration = async (configId) => {
    try {
      let configDetails = await apiClient.getConfigDetails(parseInt(configId));
      setConfigId(configId);
      const status = configDetails['status'];
      const isComplete = status === 'Complete';
      const isReceivedSections = status === 'Received Sections';
      const isReceivedSectionsText = status === 'Received Sections Text';
      const hasQuestions =
        configDetails.questions &&
        Object.keys(configDetails.questions).length > 0;
      setProgressPercentage(0);
      if (hasQuestions) {
        setModalOpen(true);
        setModalText(EXISTING_TEMPLATE_OVERWRITE_WARNING_STATUS);
        setModalButtons([
          {
            label: 'Overwrite instructions',
            color: 'grey',
            onClick: async () => {
              handleCloseModal();
              await generateTemplate(configId); // Wait for template generation to complete
            },
          },
          {
            label: 'View Template',
            color: 'primary',
            onClick: () => {
              navigate(`/User/templates/manager#${configId}`);
            },
          },
        ]);
      } else {
        if (isComplete || isReceivedSections || isReceivedSectionsText) {
          // Show confirmation modal if template already exists
          setModalOpen(true);
          setModalText(
            isComplete
              ? OVERWRITE_INSTRUCTION_STATUS
              : OVERWRITE_SECTIONS_STATUS,
          );
          setModalButtons([
            {
              label: isComplete ? 'Overwrite instructions' : 'Start Over',
              color: 'grey',
              onClick: async () => {
                handleCloseModal();
                await generateTemplate(configId); // Wait for template generation to complete
              },
            },
            {
              label: isComplete ? 'View Template' : 'Resume',
              color: 'primary',
              onClick: () => {
                if (isComplete) {
                  navigate(`/User/templates/manager#${configId}`);
                } else if (isReceivedSections) {
                  setSectionNames(Object.keys(configDetails.training_data));
                  setIsPdfSectionModalOpen(true);
                } else {
                  setSectionNames(Object.keys(configDetails.training_data));
                  setSectionsText(configDetails.training_data);
                  setIsPdfSectionsTextModalOpen(true);
                }
              },
            },
          ]);
        } else {
          await generateTemplate(configId);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setModalText(FAILED_GENERATION_MESSAGE);
    }
  };

  // Utility function to handle the process with progress updates and polling
  const runProcessWithProgress = async ({
    configId,
    statusMessages,
    fetchData,
    checkStatus,
    successCondition,
    onSuccess,
    onFail,
    onTimeoutCheck,
    type,
  }) => {
    let maxPollingDuration = 3 * 60 * 1000; // Default maximum polling duration: 2 minutes
    setConfigId(configId);
    setProgressPercentage(0);
    setModalText(`${statusMessages[0]} (5%)`);
    setModalOpen(true); // Open progress modal first

    let currentStatusIndex = 0;
    let lastProgressUpdate = 0;
    let currentProgress = 5;

    // Sleep utility
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Progress update loop
    const updateProgress = async () => {
      while (currentProgress < 90) {
        currentProgress = Math.min(currentProgress + 20, 90);
        setProgressPercentage(currentProgress);

        if (currentProgress !== lastProgressUpdate) {
          lastProgressUpdate = currentProgress;
          setModalText(
            `${statusMessages[currentStatusIndex]} (${currentProgress}%)`,
          );
          currentStatusIndex = (currentStatusIndex + 1) % statusMessages.length;
        }
        if (type === 'sectionText') {
          await sleep(3000);
        } else {
          await sleep(2000);
        }
      }
    };

    // Start progress updates in parallel
    const progressPromise = updateProgress();

    let response = null;
    try {
      response = await fetchData(); // Attempt to fetch initial data
    } catch (error) {
      console.log('Error fetching data:', error);
      if (error) {
        setModalText(TIME_OUT_MESSAGE); // Show timeout message

        // Start polling after timeout
        const pollingIntervalId = setInterval(async () => {
          try {
            const statusResponse = await checkStatus(); // Check status while polling
            if (
              statusResponse &&
              onTimeoutCheck(statusResponse, pollingIntervalId)
            ) {
              clearInterval(pollingIntervalId); // Stop polling once successful
            }
          } catch (error) {
            console.error('Error polling status:', error);
            onFail(); // Handle polling failure
            clearInterval(pollingIntervalId); // Stop polling if error occurs
          }
        }, 10000); // Polling every 10 seconds

        // Set up a timeout to stop polling after max duration
        const pollingTimeoutId = setTimeout(() => {
          clearInterval(pollingIntervalId);
          setModalText(FAILED_GENERATION_MESSAGE); // Show timeout message
          onFail(); // Handle timeout scenario
        }, maxPollingDuration);

        // Cleanup polling and timeout on component unmount
        return () => {
          clearInterval(pollingIntervalId);
          clearTimeout(pollingTimeoutId);
        };
      }
    }

    await progressPromise;

    // Check if the response is successful (outside of the try-catch)
    if (response && successCondition(response)) {
      console.log('Response:', response);
      onSuccess(response); // Execute onSuccess if successful
      return;
    } else {
      setModalText(FAILED_GENERATION_MESSAGE);
      onFail();
    }
  };

  // Function to extract sections from PDFs
  const generateTemplate = async (configId) => {
    setModalOpen(true); // Open progress modal first

    await runProcessWithProgress({
      configId,
      statusMessages: SECTION_EXTRACTION_STATUS,
      fetchData: () => apiClient.getSections(configId),
      checkStatus: () => apiClient.getConfigDetails(parseInt(configId)),
      successCondition: (response) =>
        response.sections && Object.keys(response.sections).length > 0,
      onTimeoutCheck: (rsp, pollingIntervalId) => {
        if (rsp.status === 'Received Sections') {
          const sectionData = Object.keys(rsp.training_data);
          setSectionNames(sectionData);
          setModalOpen(false);
          setIsPdfSectionModalOpen(true);
          clearInterval(pollingIntervalId);
        }
      },
      onSuccess: (response) => {
        const sectionData = Object.keys(response.sections);
        setSectionNames(sectionData);
        setModalOpen(false);
        setIsPdfSectionModalOpen(true);
      },
      onFail: () => {
        setModalText(FAILED_GENERATION_MESSAGE);
        setProgressPercentage(0);
        setModalOpen(false);
      },
      type: 'section',
    });
  };

  // Function to handle section text extraction
  const handleSaveSectionNames = async (configId, sectionNames) => {
    setSectionNames(sectionNames);
    setModalOpen(true); // Open progress modal first

    runProcessWithProgress({
      configId,
      statusMessages: SECTION_TEXT_EXTRACTION_STATUS,
      fetchData: () => apiClient.getSectionsText(configId, sectionNames),
      checkStatus: () => apiClient.getConfigDetails(parseInt(configId)),
      successCondition: (response) =>
        response.sections_text &&
        Object.keys(response.sections_text).length > 0,
      onTimeoutCheck: (rsp, pollingIntervalId) => {
        if (rsp.status === 'Received Sections Text') {
          setSectionsText(rsp.training_data); // Set the sections text
          setModalOpen(false); // Close the modal
          setIsPdfSectionsTextModalOpen(true); // Open section modal
          clearInterval(pollingIntervalId);
        }
      },
      onSuccess: (response) => {
        setSectionsText(response.sections_text);
        setModalOpen(false); // Close the modal
        setIsPdfSectionsTextModalOpen(true);
      },
      onFail: () => {
        setModalText(FAILED_GENERATION_MESSAGE);
        setProgressPercentage(0);
        setModalOpen(false);
      },
      type: 'sectionText',
    });
  };

  const handleSaveSectionTexts = async (configId, sections) => {
    setSectionsText(sections);
    const trimmedSectionTexts = Object.entries(sections).reduce(
      (acc, [section, texts]) => {
        acc[section] = texts
          .map((textObj) => Object.values(textObj)[0].trim()) // Extract and trim each text value
          .filter(Boolean); // Remove empty strings
        return acc;
      },
      {},
    );
    setModalOpen(true);

    runProcessWithProgress({
      configId,
      statusMessages: SECTION_INSTRUCTION_STATUS,
      fetchData: () =>
        apiClient.getSectionsInstructions(configId, trimmedSectionTexts),
      checkStatus: () => apiClient.getConfigDetails(parseInt(configId)),
      successCondition: (response) => Object.keys(response || {}).length > 0,
      onTimeoutCheck: (rsp, pollingIntervalId) => {
        if (rsp.status === 'Complete') {
          setModalText('Success! Redirecting to your instructions...');
          setIsPdfInstructionsModalOpen(true);
          setSectionsInstructions(rsp.questions);
          setModalOpen(false);
          clearInterval(pollingIntervalId);
        }
      },
      onSuccess: (response) => {
        setModalText('Success! Redirecting to your instructions...');
        setIsPdfInstructionsModalOpen(true);
        setSectionsInstructions(transformData(response));
        setModalOpen(false);
      },
      onFail: () => {
        setModalText(FAILED_GENERATION_MESSAGE);
        setProgressPercentage(0);
        setModalOpen(false);
      },
      type: 'sectionInstructions',
    });
  };

  const handleSaveSectionInstructions = async (configId, instructions) => {
    setModalOpen(true);

    runProcessWithProgress({
      configId,
      statusMessages: TEMPLATE_GENERATION_STATUS,
      fetchData: () =>
        apiClient.editConfig(
          parseInt(configId),
          'update_prompt',
          '',
          instructions,
          '',
        ),
      checkStatus: () => apiClient.getConfigDetails(parseInt(configId)),
      successCondition: (response) => Object.keys(response || {}).length > 0,
      onTimeoutCheck: (rsp, pollingIntervalId) => {
        setModalText('Success! Redirecting to your template...');
        navigate(`/User/templates/manager#${configId}`);
        setModalOpen(false);
        clearInterval(pollingIntervalId);
      },
      onSuccess: (response) => {
        setModalText('Success! Redirecting to your template...');
        navigate(`/User/templates/manager#${configId}`);
        setModalOpen(false);
      },
      onFail: () => {
        setModalText(FAILED_GENERATION_MESSAGE);
        setProgressPercentage(0);
        setModalOpen(false);
      },
    });
  };

  const transformData = (rsp) => {
    const output = {};
    for (const key in rsp) {
      if (rsp[key].Prompt) {
        output[key] = rsp[key].Prompt;
      }
    }
    return output;
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setProgressPercentage(0);
    setModalButtons([]); // Reset modalButtons to an empty array
    setModalHeader('PDF Template Builder');
  };

  const handleFilesSelected = (files) => {
    setShowLoader(true);
    // Loop through the files array
    const updatedFiles = Array.from(files).map((file) => {
      // Replace spaces in the file name with a rarely used character, e.g., '@@'
      const updatedFileName = file.name.replace(/\s/g, '__'); // Replace all spaces with double underscore

      // Create a new file object with the updated name (Optional if you need to use updated file name)
      const updatedFile = new File([file], updatedFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      return updatedFile;
    });
    apiClient
      .getUploadUrlsForPDF(configId, updatedFiles)
      .then(() => {
        fetchPDFBuilderDocs();
        setShowLoader(false);
      })
      .catch((error) => {
        console.error('Error during file upload process:', error);
      });
  };

  const handleDownload = (configId, documentName) => {
    apiClient
      .downloadPDFBuilderDoc(configId, documentName)
      .then((response) => {
        const downloadUrl = response?.url;
        if (downloadUrl) {
          // Open the URL in a new tab
          window.open(downloadUrl, '_blank'); // Opens in a new tab
        }
      })
      .catch((error) => {
        console.error('Error fetching agreement details:', error);
      });
  };

  const handleConfirmAction = async () => {
    try {
      let response;
      const configId = confirmationDetails.key;
      const documentName = confirmationDetails.example || '';
      if (confirmationDetails.type === 'example') {
        response = await apiClient.deleteDocument(configId, documentName);

        if (response.result === 'success') {
          setNoteTypeData((prevData) => {
            const updatedNoteType = prevData.map((noteType) => {
              if (noteType.configId === configId) {
                // Filter out the deleted document from examples
                const updatedExamples = noteType.configDocs.filter(
                  (example) => example !== documentName,
                );
                return {
                  ...noteType,
                  configDocs: updatedExamples,
                };
              }
              return noteType;
            });
            return updatedNoteType;
          });
        } else {
          console.error(
            'An error occurred while deleting the document. Please try again.',
          );
        }
      } else if (confirmationDetails.type === 'noteType') {
        const response = await apiClient.deleteDocument(configId, ''); // Pass an empty string for documentName to delete all

        // Check if the deletion was successful
        if (response.result === 'success') {
          // Remove the entire noteType from the state
          setNoteTypeData((prevData) => {
            const updatedNoteType = prevData.filter(
              (noteType) => noteType.configId !== configId,
            );
            return updatedNoteType;
          });
        }
      } else {
        console.error('Failed to delete noteType:', response);
        setModalText(
          'An error occurred while deleting the note type. Please try again.',
        );
      }
    } catch (error) {
      console.error('Error during the delete process:', error);
    } finally {
      setConfirmationDetails({
        isOpen: false,
        key: null,
        example: null,
        type: null,
      });
    }
  };

  const NoteTypeExamples = ({
    examples,
    configId,
    onExampleChange,
    onDownload,
    onDelete,
  }) => {
    const containerRef = useRef(null); // Reference to the container holding the examples

    // Scroll to the bottom whenever examples are updated
    useEffect(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, [examples]); // Run this effect whenever 'examples' change

    return (
      <div
        ref={containerRef} // Attach the ref to the container
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        {Array.isArray(examples)
          ? examples.map((example, exampleIndex) => (
              <div
                key={exampleIndex} // Use exampleIndex as the key
                style={{
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <EditableLabel
                  labelText={`Example ${exampleIndex + 1}`}
                  text={example} // Use example directly since it's the example text
                  onChange={(newText) => onExampleChange(newText, exampleIndex)}
                  disabled={true}
                />
                <Button
                  className="fa fa-download"
                  size="md" // Adjust button size for the main button
                  onClick={() => onDownload(configId, example)}
                />
                <Button
                  className="fa fa-trash"
                  size="md" // Adjust button size for the main button
                  onClick={() => onDelete(configId, example)}
                />
              </div>
            ))
          : 'No samples available'}
      </div>
    );
  };

  return (
    <>
      <Header isLite={true} />
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        buttons={modalButtons}
        isCloseButton={false}
      >
        <h2>{modalHeader}</h2>
        <p>{modalText}</p>
        <ModalBody>
          {progressPercentage > 0 && progressPercentage < 100 ? (
            <LinearProgressBar percentage={progressPercentage} />
          ) : (
            ''
          )}
        </ModalBody>
      </Modal>

      {/* Page content */}
      <Container className="mt--9" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '10px',
                  }}
                >
                  <Link to={`/User/settings`}>
                    <Button
                      className="fa fa-arrow-left"
                      color="primary"
                      size={isSmallScreen ? 'sm' : 'md'} // Adjust button size based on screen size
                      title="User Settings"
                    />
                  </Link>
                  <h1
                    className="mb-0"
                    style={{
                      fontSize: isSmallScreen ? '1.2rem' : '1.6rem', // Adjust font size for small screens
                      fontWeight: 'bold',
                      color: '#343a40',
                    }}
                  >
                    PDF Template Builder
                  </h1>
                  <div style={{ marginTop: '10px', textAlign: 'left' }}>
                    <Button
                      color="primary"
                      size={isSmallScreen ? 'sm' : 'md'} // Adjust button size for the main button
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={openHelpModal}
                    >
                      <span style={{ marginRight: '5px' }}>
                        <span className="fa fa-question-circle fa-2x" />
                      </span>
                      Help
                    </Button>
                  </div>
                </div>
                {status && (
                  <div
                    style={{
                      margin: '20px 0',
                      textAlign: 'center',
                      fontSize: '14px',
                      color: '#6c757d',
                      backgroundColor: '#f8f9fa',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #d3d3d3',
                      animation: 'fadeIn 0.5s',
                    }}
                  >
                    {status}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '20px',
                  }}
                >
                  <Button
                    color="primary"
                    size={isSmallScreen ? 'md' : 'md'} // Adjust button size for the main button
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: isSmallScreen ? '8px 16px' : '12px 20px', // Adjust padding for different screen sizes
                      fontSize: isSmallScreen ? '14px' : '16px', // Adjust font size for different screen sizes
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => setIsAddNoteTypeModalOpen(true)}
                    id="add-note-type-button"
                  >
                    <span
                      className="fa fa-plus-circle mr-2"
                      style={{
                        fontSize: isSmallScreen ? '1.2rem' : '1.5rem',
                        color: '#ffffff',
                      }} // Adjust icon size for small screens
                    />
                    Add Note Type
                  </Button>

                  <AddNoteTypeModal
                    isOpen={isAddNoteTypeModalOpen}
                    onClose={() => setIsAddNoteTypeModalOpen(false)}
                    onAdd={addNoteType}
                    type="pdf"
                  />
                </div>
              </CardHeader>

              <div className="list-container">
                <div className="list-header" style={{ marginBottom: '10px' }}>
                  <strong></strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                ></div>

                {Array.isArray(noteTypeData) &&
                  noteTypeData.map((noteType, index) => (
                    <div
                      style={{
                        margin: '10px',
                      }}
                      key={noteType.configId}
                    >
                      <CollapsibleCore
                        key={noteType.configId}
                        id={noteType.configId}
                        label={
                          <div style={{ margin: '10px' }}>
                            {noteType.configName}{' '}
                            {noteType.configDocs.length <=
                            MIN_ONE_NOTE_TYPE_EXAMPLES
                              ? `(${noteType.configDocs.length || 0}/${MIN_ONE_NOTE_TYPE_EXAMPLES})`
                              : `(${noteType.configDocs.length || 0}/${noteType.configDocs.length})`}
                          </div>
                        }
                        shownChildren={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              margin: '10px',
                            }}
                          >
                            <Button
                              color="primary"
                              size={isSmallScreen ? 'sm' : 'md'} // Adjust button size for the main button
                              style={{
                                fontSize: isSmallScreen ? '.6rem' : '1rem',
                                color: '#ffffff',
                              }} // Adjust icon size for small screens
                              disabled={
                                noteType.configDocs.length <
                                  MIN_ONE_NOTE_TYPE_EXAMPLES ||
                                noteType.configDocs.length > N_EXAMPLES
                              }
                              onClick={() => {
                                handleTemplateGeneration(noteType.configId);
                                console.log(
                                  `Generating template for ${noteType.configName}`,
                                );
                              }}
                            >
                              <span className="fa fa-file" />{' '}
                              {noteType.status === 'Complete'
                                ? 'Resume Template'
                                : 'Generate Template'}
                            </Button>
                            <Button
                              className="fa fa-plus"
                              size={isSmallScreen ? 'sm' : 'md'} // Adjust button size for the main button
                              onClick={() => {
                                setIsPdfModalOpen(true);
                                setConfigId(noteType.configId);
                              }}
                            />{' '}
                          </div>
                        }
                        innerChildren={
                          <NoteTypeExamples
                            examples={noteType.configDocs}
                            configId={noteType.configId}
                            onExampleChange={(newText, exampleIndex) => {
                              const updatedExamples = [...noteType.configDocs];
                              updatedExamples[exampleIndex] = newText;
                              setNoteTypeData((prevData) =>
                                prevData.map((item) =>
                                  item.configId === noteType.configId
                                    ? { ...item, configDocs: updatedExamples }
                                    : item,
                                ),
                              );
                            }}
                            onDownload={handleDownload}
                            onDelete={(configId, example) => {
                              setConfirmationDetails({
                                isOpen: true,
                                key: noteType.configId,
                                example,
                                type: 'example',
                              });
                            }}
                          />
                        }
                        onAddClick={() => {
                          const updatedExamples = [...noteType.configDocs]; // Copy the array
                          setNoteTypeData((prevData) =>
                            prevData.map((item) =>
                              item.configId === noteType.configId
                                ? { ...item, configDocs: updatedExamples }
                                : item,
                            ),
                          );
                        }}
                        border={
                          noteType.configDocs.length === 0 ||
                          noteType.configDocs.length <
                            MIN_ONE_NOTE_TYPE_EXAMPLES
                            ? '2px solid red' // Red border if less than required examples
                            : '2px solid #d5d5d5' // Default border for valid case
                        }
                        deleteButton={
                          <div style={{ margin: '10px' }}>
                            <Button
                              className="fa fa-trash"
                              size={isSmallScreen ? 'sm' : 'md'} // Adjust button size for the main button
                              onClick={() => {
                                setConfirmationDetails({
                                  isOpen: true,
                                  key: noteType.configId,
                                  type: 'noteType', // Indicate note type deletion
                                });
                              }}
                            />
                          </div>
                        }
                      />
                    </div>
                  ))}
              </div>
            </Card>
          </div>
        </Row>
      </Container>
      {isPdfModalOpen && (
        <PdfUploadModal
          isOpen={isPdfModalOpen}
          onClose={() => setIsPdfModalOpen(false)} // Close modal when it's not needed
          onFileSelected={handleFilesSelected} // Handle file selected event
          noteTypeData={
            noteTypeData.find((item) => item.configId === configId)
              ?.configDocs || []
          } // Pass the examples for the noteType
          minNumberNoteType={MIN_ONE_NOTE_TYPE_EXAMPLES}
          maxNumberNoteType={N_EXAMPLES}
          generateTemplate={() => handleTemplateGeneration(configId)} // Pass as a function reference
          innerChildren={
            <NoteTypeExamples
              examples={
                noteTypeData.find((item) => item.configId === configId)
                  ?.configDocs || []
              } // Find the correct item using configId
              configId={configId}
              onExampleChange={(newText, exampleIndex) => {
                setNoteTypeData((prevData) =>
                  prevData.map((item) => {
                    if (item.configId === configId) {
                      const updatedExamples = [...item.configDocs]; // Copy the existing configDocs array
                      updatedExamples[exampleIndex] = newText; // Update the specific example
                      return { ...item, configDocs: updatedExamples }; // Return the updated item
                    }
                    return item; // Return the unchanged items
                  }),
                );
              }}
              onDownload={handleDownload}
              onDelete={(noteType, example) => {
                setConfirmationDetails({
                  isOpen: true,
                  key: noteType,
                  example,
                  type: 'example',
                });
              }}
            />
          }
          showLoader={showLoader}
          buttonText={
            noteTypeData.find((item) => item.configId === configId)?.status ===
            'Complete'
              ? 'Resume Template'
              : 'Generate Template'
          }
        />
      )}
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ ...confirmationDetails, isOpen: false })
        }
        onConfirm={handleConfirmAction}
      >
        {confirmationDetails.type === 'example'
          ? 'Are you sure you want to delete this example?'
          : 'Are you sure you want to delete this note type? This will delete your customized template as well.'}
      </ConfirmationModal>
      {isPdfSectionModalOpen && (
        <PdfSectionModal
          isOpen={isPdfSectionModalOpen}
          onClose={() => setIsPdfSectionModalOpen(false)}
          configId={configId}
          sections={sectionNames}
          onSave={handleSaveSectionNames}
        />
      )}
      {isPdfSectionsTextModalOpen && (
        <PdfSectionsTextModal
          isOpen={isPdfSectionsTextModalOpen}
          onClose={() => setIsPdfSectionsTextModalOpen(false)}
          configId={configId}
          sections={sectionNames}
          sectionsText={sectionsText}
          onSave={handleSaveSectionTexts}
          onBack={() => {
            setIsPdfSectionsTextModalOpen(false);
            setIsPdfSectionModalOpen(true);
          }}
          pdfBuilderDocs={
            noteTypeData.find((item) => item.configId === configId)
              ?.configDocs || []
          }
        />
      )}
      {isPdfInstructionsModalOpen && (
        <PdfInstructionsModal
          isOpen={isPdfInstructionsModalOpen}
          onClose={() => setIsPdfInstructionsModalOpen(false)}
          configId={configId}
          sectionsInstructions={sectionsInstructions}
          onSave={handleSaveSectionInstructions}
          onBack={(rsp) => {
            setIsPdfInstructionsModalOpen(false);
            if (rsp === 0) {
              setIsPdfSectionModalOpen(true);
            } else if (rsp === 1) {
              setIsPdfSectionsTextModalOpen(true);
            }
          }}
        />
      )}
    </>
  );
};

export default PdfTemplateBuilder;
